import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import {
  AGENT_MODEL_OPTIONS,
  AGENT_MODEL_SURFACES,
  DEFAULT_AGENT_MODEL_ID,
  filterAgentModelSurfacesForActiveNav,
  GitHubAppInstallationSummary,
  IntegrationProvider,
  IntegrationSnapshot,
  IntegrationsService,
  ReposGitHubService,
  SettingsService,
  ThemeService,
  ToastService,
} from '@services';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './settings.page.html',
  styleUrls: ['./settings.page.scss'],
})
export class SettingsPage implements OnInit {
  private static readonly PERSONAL_GITHUB_INSTALLATION_ID_KEY = 'settings.personalGithubInstallationId';
  private static readonly PERSONAL_GITHUB_ACCOUNT_LOGIN_KEY = 'settings.personalGithubAccountLogin';
  private static readonly PENDING_INSTALL_SOURCE_KEY = 'repos.pendingInstallSource';
  isProduction = environment.production;
  providers: IntegrationProvider[] = ['google-genai', 'openai', 'anthropic', 'xai'];
  integrationsLoading = false;
  integrationBusy: Record<IntegrationProvider, boolean> = {
    'google-genai': false,
    openai: false,
    anthropic: false,
    xai: false,
  };
  providerInputs: Record<IntegrationProvider, string> = {
    'google-genai': '',
    openai: '',
    anthropic: '',
    xai: '',
  };
  integrations: IntegrationSnapshot | null = null;
  readonly modelOptions = AGENT_MODEL_OPTIONS;
  readonly modelSurfaces = filterAgentModelSurfacesForActiveNav(AGENT_MODEL_SURFACES);
  readonly fallbackModelId = DEFAULT_AGENT_MODEL_ID;
  integrationPreferences = {
    defaultProvider: '' as '' | IntegrationProvider,
    defaultModel: '',
    routeModelDefaultChat: '',
    routeModelDefaultTickets: '',
    surfaceModelDefaults: {} as Record<string, string>,
  };
  githubContext = {
    personalInstallationId: '',
    personalAccountLogin: '',
    activePersonalInstallationId: '',
  };
  personalGithubConnections: Array<{ installationId: string; accountLogin: string; active: boolean }> = [];
  livePersonalGithubInstallations: Array<{
    installationId: string;
    accountLogin: string;
    accountType: 'User' | 'Organization' | 'Unknown';
    saved: boolean;
    active: boolean;
  }> = [];
  githubBusinessInfo = {
    installationId: '',
    accountLogin: '',
  };
  githubConnectLoading = false;
  showManualGithubEntry = false;
  githubInstallationsLoading = false;
  private integrationsRequestVersion = 0;
  private integrationsLoadInFlight = 0;
  private readonly integrationsTracePrefix = '[integrations-trace]';
  private readonly integrationsTraceEnabled = (() => {
    try {
      return localStorage.getItem('integrationsTrace') === '1';
    } catch {
      return false;
    }
  })();

  constructor(
    public settings: SettingsService,
    public theme: ThemeService,
    private integrationsService: IntegrationsService,
    private reposGithubService: ReposGitHubService,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    this.restorePersonalGithubFromLocal();
    void this.loadIntegrations();
    void this.loadLivePersonalGithubInstallations();
  }

  toggleAutoCollapse(event: Event) {
    const target = event.target as HTMLInputElement;
    this.settings.setAutoCollapse(target.checked);
  }

  onThemeChange(mode: 'light' | 'dark' | 'system' | 'custom') {
    if (mode === 'custom') {
      this.theme.setTheme('custom', this.buildCustomVars());
    } else {
      this.theme.setTheme(mode);
    }
  }

  custom = {
    primary: '#8a00b4',
    primaryContrast: '#ffffff',
    bg: '#f8f9fa',
    surface: '#ffffff',
    border: '#e6e6e6',
    text: '#1a1a1a',
    muted: '#666666',
  };

  applyCustom() {
    this.theme.setTheme('custom', this.buildCustomVars());
  }

  resetCustom() {
    this.custom = {
      primary: '#8a00b4',
      primaryContrast: '#ffffff',
      bg: '#f8f9fa',
      surface: '#ffffff',
      border: '#e6e6e6',
      text: '#1a1a1a',
      muted: '#666666',
    };
    this.theme.clearCustomVars();
    this.theme.setTheme('light');
  }

  private buildCustomVars(): Record<string, string> {
    return {
      // Semantic tokens
      '--color-primary': this.custom.primary,
      '--color-primary-contrast': this.custom.primaryContrast,
      '--color-bg': this.custom.bg,
      '--color-surface': this.custom.surface,
      '--color-border': this.custom.border,
      '--text-primary': this.custom.text,
      '--text-muted': this.custom.muted,
      // Legacy/mapped tokens
      '--primary-color': this.custom.primary,
      '--primary-color-dark': `color-mix(in srgb, ${this.custom.primary} 70%, #000)`,
      '--primary-color-light': `color-mix(in srgb, ${this.custom.primary} 8%, transparent)`,
      // Bootstrap mappings
      '--bs-body-bg': this.custom.bg,
      '--bs-body-color': this.custom.text,
      '--bs-primary': this.custom.primary,
      '--bs-border-color': this.custom.border,
    } as Record<string, string>;
  }

  providerLabel(provider: IntegrationProvider): string {
    if (provider === 'google-genai') return 'Google Gemini';
    if (provider === 'openai') return 'OpenAI';
    if (provider === 'anthropic') return 'Anthropic';
    return 'xAI';
  }

  providerStatus(provider: IntegrationProvider) {
    return this.integrations?.providers?.find((p) => p.provider === provider) ?? null;
  }

  isProviderConnected(provider: IntegrationProvider): boolean {
    return !!this.providerStatus(provider)?.connected;
  }

  private trace(phase: string, data: Record<string, unknown> = {}) {
    if (!this.integrationsTraceEnabled) return;
    console.log(this.integrationsTracePrefix, phase, data);
  }

  private traceError(phase: string, error: any, data: Record<string, unknown> = {}) {
    if (!this.integrationsTraceEnabled) return;
    console.error(this.integrationsTracePrefix, phase, {
      ...data,
      message: error?.message ?? String(error),
      status: error?.response?.status,
      responseData: error?.response?.data,
    });
  }

  private beginLoadRequest(source: string): number {
    const requestVersion = ++this.integrationsRequestVersion;
    this.integrationsLoadInFlight += 1;
    this.integrationsLoading = true;
    this.trace('request:start', { source, requestVersion, inFlight: this.integrationsLoadInFlight });
    return requestVersion;
  }

  private endLoadRequest(source: string, requestVersion: number): void {
    this.integrationsLoadInFlight = Math.max(0, this.integrationsLoadInFlight - 1);
    this.integrationsLoading = this.integrationsLoadInFlight > 0;
    this.trace('request:end', {
      source,
      requestVersion,
      inFlight: this.integrationsLoadInFlight,
      loading: this.integrationsLoading,
    });
  }

  private applySnapshotCore(snapshot: IntegrationSnapshot) {
    this.integrations = snapshot;
    const prefs = snapshot?.preferences ?? {};
    this.integrationPreferences.defaultProvider = (prefs.defaultProvider ?? '') as '' | IntegrationProvider;
    this.integrationPreferences.defaultModel = String(prefs.defaultModel ?? '');
    this.integrationPreferences.routeModelDefaultChat = String(prefs.routeModelDefaultChat ?? '');
    this.integrationPreferences.routeModelDefaultTickets = String(prefs.routeModelDefaultTickets ?? '');
    this.integrationPreferences.surfaceModelDefaults =
      prefs.surfaceModelDefaults && typeof prefs.surfaceModelDefaults === 'object'
        ? { ...prefs.surfaceModelDefaults }
        : {};
    const snapshotAny = snapshot as any;
    const nextPersonalInstallationId =
      Number(
        snapshotAny?.github?.personal?.installationId ??
          snapshotAny?.github?.installationId ??
          snapshotAny?.githubContext?.personal?.installationId ??
          snapshotAny?.githubContext?.installationId ??
          snapshotAny?.github?.resolvedInstallationId ??
          0
      ) || 0;
    const nextPersonalAccountLogin = String(
      snapshotAny?.github?.personal?.accountLogin ??
        snapshotAny?.github?.accountLogin ??
        snapshotAny?.githubContext?.personal?.accountLogin ??
        snapshotAny?.githubContext?.accountLogin ??
        ''
    ).trim();
    const personalConnectionsRaw = Array.isArray(snapshotAny?.github?.personals) ? snapshotAny.github.personals : [];
    this.personalGithubConnections = personalConnectionsRaw
      .map((raw: any) => ({
        installationId: String(raw?.installationId ?? '').trim(),
        accountLogin: String(raw?.accountLogin ?? '').trim(),
        active: !!raw?.active,
      }))
      .filter((entry: any) => !!entry.installationId);
    this.livePersonalGithubInstallations = this.livePersonalGithubInstallations.map((entry) => {
      const savedEntry = this.personalGithubConnections.find((connection) => connection.installationId === entry.installationId);
      return {
        ...entry,
        saved: !!savedEntry,
        active: !!savedEntry?.active,
      };
    });
    const activePersonalInstallationId =
      String((snapshotAny?.github?.activePersonalInstallationId ?? nextPersonalInstallationId) || '').trim() || '';
    this.githubContext.personalInstallationId = nextPersonalInstallationId > 0 ? String(nextPersonalInstallationId) : '';
    this.githubContext.personalAccountLogin = nextPersonalAccountLogin;
    this.githubContext.activePersonalInstallationId = activePersonalInstallationId;
    if (nextPersonalInstallationId > 0) {
      this.persistPersonalGithubToLocal(this.githubContext.personalInstallationId, nextPersonalAccountLogin || this.githubContext.personalAccountLogin);
    }
    this.githubBusinessInfo.installationId = snapshot?.github?.business?.installationId ? String(snapshot.github.business.installationId) : '';
    this.githubBusinessInfo.accountLogin = String(snapshot?.github?.business?.accountLogin ?? '');
    this.trace('snapshot:applied', {
      providerStates: (snapshot?.providers ?? []).map((p) => ({
        provider: p.provider,
        connected: !!p.connected,
        hasMaskedApiKey: !!String(p.maskedApiKey ?? ''),
      })),
      lastUpdatedAt: snapshot?.lastUpdatedAt ?? null,
    });
  }

  private applySnapshotIfLatest(snapshot: IntegrationSnapshot, source: string, requestVersion: number): boolean {
    const latest = this.integrationsRequestVersion;
    const shouldApply = requestVersion === latest;
    this.trace('snapshot:received', { source, requestVersion, latest, shouldApply });
    if (!shouldApply) {
      this.trace('snapshot:skippedStale', { source, requestVersion, latest });
      return false;
    }
    this.applySnapshotCore(snapshot);
    return true;
  }

  private applyMutationSnapshot(snapshot: IntegrationSnapshot, source: string): void {
    const requestVersion = ++this.integrationsRequestVersion;
    this.trace('mutation:applyDirect', { source, requestVersion });
    this.applySnapshotIfLatest(snapshot, source, requestVersion);
  }

  private async refreshSnapshotAuthoritatively(source: string): Promise<void> {
    const requestVersion = this.beginLoadRequest(`${source}:refresh`);
    try {
      const snapshot = await this.integrationsService.getSnapshot();
      this.applySnapshotIfLatest(snapshot, `${source}:refresh`, requestVersion);
    } finally {
      this.endLoadRequest(`${source}:refresh`, requestVersion);
    }
  }

  async loadIntegrations() {
    const requestVersion = this.beginLoadRequest('loadIntegrations');
    try {
      const snapshot = await this.integrationsService.getSnapshot();
      this.applySnapshotIfLatest(snapshot, 'loadIntegrations', requestVersion);
      void this.loadLivePersonalGithubInstallations();
    } catch (e: any) {
      this.traceError('loadIntegrations:error', e, { requestVersion });
      this.toast.error(e?.message ?? 'Failed to load integrations', 'Settings');
    } finally {
      this.endLoadRequest('loadIntegrations', requestVersion);
    }
  }

  async connectPersonalGithub() {
    this.githubConnectLoading = true;
    try {
      const res = await this.reposGithubService.getInstallUrl();
      const joiner = res.url.includes('?') ? '&' : '?';
      const installUrl = `${res.url}${joiner}state=${encodeURIComponent('settings')}`;
      try {
        localStorage.setItem(SettingsPage.PENDING_INSTALL_SOURCE_KEY, 'settings');
      } catch {}
      window.open(installUrl, '_blank', 'noopener,noreferrer');
      this.toast.info('Finish the GitHub App connection flow to save your personal GitHub account.', 'Settings');
    } catch (e: any) {
      this.traceError('connectPersonalGithub:error', e);
      this.toast.error(e?.message ?? 'Failed to start GitHub connection', 'Settings');
    } finally {
      this.githubConnectLoading = false;
    }
  }

  async loadLivePersonalGithubInstallations(showErrorToast = false) {
    this.githubInstallationsLoading = true;
    try {
      const installations = await this.reposGithubService.listInstallations();
      this.livePersonalGithubInstallations = this.mapLivePersonalGithubInstallations(installations);
    } catch (e: any) {
      this.traceError('loadLivePersonalGithubInstallations:error', e);
      this.livePersonalGithubInstallations = [];
      if (showErrorToast) {
        this.toast.error(e?.message ?? 'Failed to load GitHub installations', 'Settings');
      }
    } finally {
      this.githubInstallationsLoading = false;
    }
  }

  async saveProvider(provider: IntegrationProvider) {
    const apiKey = String(this.providerInputs[provider] ?? '').trim();
    if (!apiKey) {
      this.toast.warning('Enter an API key first', this.providerLabel(provider));
      return;
    }
    this.integrationBusy[provider] = true;
    try {
      await this.integrationsService.setProviderApiKey(provider, apiKey);
      this.trace('provider:saveMutationDone', { provider });
      await this.refreshSnapshotAuthoritatively(`saveProvider:${provider}`);
      const persistedConnected = !!this.providerStatus(provider)?.connected;
      if (!persistedConnected) {
        this.toast.error(
          `${this.providerLabel(provider)} key did not persist. Check machine-agent-api INTEGRATIONS_MASTER_KEY and restart backend.`,
          'Settings'
        );
        return;
      }
      this.providerInputs[provider] = '';
      this.toast.success(`${this.providerLabel(provider)} key saved`, 'Settings');
    } catch (e: any) {
      this.traceError('provider:saveError', e, { provider });
      this.toast.error(e?.message ?? 'Failed to save key', this.providerLabel(provider));
    } finally {
      this.integrationBusy[provider] = false;
    }
  }

  async removeProvider(provider: IntegrationProvider) {
    this.integrationBusy[provider] = true;
    try {
      await this.integrationsService.removeProviderApiKey(provider);
      this.trace('provider:removeMutationDone', { provider });
      await this.refreshSnapshotAuthoritatively(`removeProvider:${provider}`);
      this.toast.success(`${this.providerLabel(provider)} disconnected`, 'Settings');
    } catch (e: any) {
      this.traceError('provider:removeError', e, { provider });
      this.toast.error(e?.message ?? 'Failed to disconnect provider', this.providerLabel(provider));
    } finally {
      this.integrationBusy[provider] = false;
    }
  }

  async saveIntegrationPreferences() {
    try {
      const snapshot = await this.integrationsService.updatePreferences({
        defaultProvider: this.integrationPreferences.defaultProvider || undefined,
        defaultModel: String(this.integrationPreferences.defaultModel ?? '').trim() || undefined,
        routeModelDefaultChat: String(this.integrationPreferences.routeModelDefaultChat ?? '').trim() || undefined,
        routeModelDefaultTickets: String(this.integrationPreferences.routeModelDefaultTickets ?? '').trim() || undefined,
        surfaceModelDefaults: { ...this.integrationPreferences.surfaceModelDefaults },
      });
      this.applyMutationSnapshot(snapshot, 'saveIntegrationPreferences');
      await this.refreshSnapshotAuthoritatively('saveIntegrationPreferences');
      this.toast.success('Default model preferences updated', 'Settings');
    } catch (e: any) {
      this.traceError('saveIntegrationPreferences:error', e);
      this.toast.error(e?.message ?? 'Failed to save preferences', 'Settings');
    }
  }

  async saveGithubContext() {
    try {
      const personalInstallationIdNum = Number(String(this.githubContext.personalInstallationId ?? '').trim());
      const personalInstallationId =
        Number.isFinite(personalInstallationIdNum) && personalInstallationIdNum > 0 ? personalInstallationIdNum : undefined;
      if (!personalInstallationId) {
        this.toast.warning('Select a valid personal installation first', 'Settings');
        return;
      }
      this.persistPersonalGithubToLocal(String(personalInstallationId), String(this.githubContext.personalAccountLogin ?? '').trim());
      const nextConnections = this.mergePersonalConnections([
        ...this.personalGithubConnections,
        {
          installationId: String(personalInstallationId),
          accountLogin: String(this.githubContext.personalAccountLogin ?? '').trim(),
          active: true,
        },
      ]);
      const snapshot = await this.integrationsService.updateGithubContext({
        activeInstallationMode: 'personal',
        personal: {
          installationId: personalInstallationId,
          accountLogin: String(this.githubContext.personalAccountLogin ?? '').trim() || undefined,
        },
        personals: nextConnections.map((entry) => ({
          installationId: Number(entry.installationId),
          accountLogin: entry.accountLogin || undefined,
        })),
        activePersonalInstallationId: personalInstallationId,
      });
      this.applyMutationSnapshot(snapshot, 'saveGithubContext');
      const snapshotAny = snapshot as any;
      const persistedFromMutation =
        Number(
          snapshotAny?.github?.personal?.installationId ??
            snapshotAny?.github?.installationId ??
            snapshotAny?.githubContext?.personal?.installationId ??
            snapshotAny?.githubContext?.installationId ??
            0
        ) || 0;
      const mutationIndicatesPersisted =
        persistedFromMutation > 0 || !!snapshotAny?.github?.personal?.connected || !!snapshotAny?.github?.connected;
      if (!mutationIndicatesPersisted) {
        this.trace('saveGithubContext:mutationNoPersistSignal', { personalInstallationId });
      }
      try {
        await this.refreshSnapshotAuthoritatively('saveGithubContext');
      } catch (refreshError: any) {
        this.traceError('saveGithubContext:refreshError', refreshError);
      }
      void this.loadLivePersonalGithubInstallations();
      const persistedInstallationId =
        Number(this.integrations?.github?.personal?.installationId ?? 0) || persistedFromMutation || personalInstallationId;
      this.persistPersonalGithubToLocal(String(persistedInstallationId), String(this.githubContext.personalAccountLogin ?? '').trim());
      this.toast.success(`Personal GitHub saved (#${persistedInstallationId})`, 'Settings');
    } catch (e: any) {
      this.traceError('saveGithubContext:error', e);
      this.toast.error(e?.message ?? 'Failed to save GitHub context', 'Settings');
    }
  }

  async clearGithubContext() {
    try {
      const snapshot = await this.integrationsService.clearGithubContext();
      this.applyMutationSnapshot(snapshot, 'clearGithubContext');
      this.clearPersonalGithubFromLocal();
      this.showManualGithubEntry = false;
      void this.loadLivePersonalGithubInstallations();
      this.toast.success('GitHub context cleared', 'Settings');
    } catch (e: any) {
      this.traceError('clearGithubContext:error', e);
      this.toast.error(e?.message ?? 'Failed to clear GitHub context', 'Settings');
    }
  }

  async setActivePersonalGithubConnection(installationIdRaw: string) {
    const installationId = Number(String(installationIdRaw ?? '').trim());
    if (!Number.isFinite(installationId) || installationId <= 0) return;
    const target = this.personalGithubConnections.find((entry) => Number(entry.installationId) === installationId);
    if (!target) return;
    try {
      const snapshot = await this.integrationsService.updateGithubContext({
        activeInstallationMode: 'personal',
        personal: {
          installationId,
          accountLogin: target.accountLogin || undefined,
        },
        personals: this.personalGithubConnections.map((entry) => ({
          installationId: Number(entry.installationId),
          accountLogin: entry.accountLogin || undefined,
        })),
        activePersonalInstallationId: installationId,
      });
      this.applyMutationSnapshot(snapshot, 'setActivePersonalGithubConnection');
      this.persistPersonalGithubToLocal(String(installationId), target.accountLogin);
      void this.loadLivePersonalGithubInstallations();
      this.toast.success(`Active personal GitHub set to ${target.accountLogin || `#${installationId}`}`, 'Settings');
    } catch (e: any) {
      this.traceError('setActivePersonalGithubConnection:error', e, { installationId });
      this.toast.error(e?.message ?? 'Failed to switch personal GitHub account', 'Settings');
    }
  }

  async removePersonalGithubConnection(installationIdRaw: string) {
    const installationId = Number(String(installationIdRaw ?? '').trim());
    if (!Number.isFinite(installationId) || installationId <= 0) return;
    const nextConnections = this.personalGithubConnections.filter((entry) => Number(entry.installationId) !== installationId);
    if (nextConnections.length === 0) {
      await this.clearGithubContext();
      return;
    }
    const nextActive = nextConnections.find((entry) => entry.active) ?? nextConnections[0];
    try {
      const snapshot = await this.integrationsService.updateGithubContext({
        activeInstallationMode: 'personal',
        personal: {
          installationId: Number(nextActive.installationId),
          accountLogin: nextActive.accountLogin || undefined,
        },
        personals: nextConnections.map((entry) => ({
          installationId: Number(entry.installationId),
          accountLogin: entry.accountLogin || undefined,
        })),
        activePersonalInstallationId: Number(nextActive.installationId),
      });
      this.applyMutationSnapshot(snapshot, 'removePersonalGithubConnection');
      this.persistPersonalGithubToLocal(nextActive.installationId, nextActive.accountLogin);
      void this.loadLivePersonalGithubInstallations();
      this.toast.success('Personal GitHub account removed', 'Settings');
    } catch (e: any) {
      this.traceError('removePersonalGithubConnection:error', e, { installationId });
      this.toast.error(e?.message ?? 'Failed to remove personal GitHub account', 'Settings');
    }
  }

  private mergePersonalConnections(
    entries: Array<{ installationId: string; accountLogin: string; active: boolean }>
  ): Array<{ installationId: string; accountLogin: string; active: boolean }> {
    const map = new Map<string, { installationId: string; accountLogin: string; active: boolean }>();
    for (const entry of entries) {
      const installationId = String(entry?.installationId ?? '').trim();
      if (!installationId) continue;
      map.set(installationId, {
        installationId,
        accountLogin: String(entry?.accountLogin ?? '').trim(),
        active: !!entry?.active,
      });
    }
    const out = Array.from(map.values());
    const active = out.find((entry) => entry.active) ?? out[0];
    return out.map((entry) => ({ ...entry, active: entry.installationId === active?.installationId }));
  }

  private mapLivePersonalGithubInstallations(installations: GitHubAppInstallationSummary[]): Array<{
    installationId: string;
    accountLogin: string;
    accountType: 'User' | 'Organization' | 'Unknown';
    saved: boolean;
    active: boolean;
  }> {
    const savedByInstallationId = new Map(this.personalGithubConnections.map((entry) => [entry.installationId, entry]));
    return (Array.isArray(installations) ? installations : [])
      .filter((entry) => String(entry?.accountType ?? '').trim() === 'User')
      .map((entry) => {
        const installationId = String(entry?.installationId ?? '').trim();
        const saved = savedByInstallationId.has(installationId);
        const savedEntry = savedByInstallationId.get(installationId);
        return {
          installationId,
          accountLogin: String(entry?.accountLogin ?? '').trim(),
          accountType: entry?.accountType ?? 'Unknown',
          saved,
          active: !!savedEntry?.active,
        };
      })
      .filter((entry) => !!entry.installationId)
      .sort((a, b) => {
        if (a.active !== b.active) return a.active ? -1 : 1;
        if (a.saved !== b.saved) return a.saved ? -1 : 1;
        return a.accountLogin.localeCompare(b.accountLogin);
      });
  }

  private persistPersonalGithubToLocal(installationIdRaw: string, accountLoginRaw: string): void {
    const installationId = String(installationIdRaw ?? '').trim();
    if (!installationId) return;
    const accountLogin = String(accountLoginRaw ?? '').trim();
    try {
      localStorage.setItem(SettingsPage.PERSONAL_GITHUB_INSTALLATION_ID_KEY, installationId);
      if (accountLogin) {
        localStorage.setItem(SettingsPage.PERSONAL_GITHUB_ACCOUNT_LOGIN_KEY, accountLogin);
      }
    } catch {
      // Ignore local storage failures.
    }
  }

  private restorePersonalGithubFromLocal(): void {
    try {
      const installationId = String(localStorage.getItem(SettingsPage.PERSONAL_GITHUB_INSTALLATION_ID_KEY) ?? '').trim();
      const accountLogin = String(localStorage.getItem(SettingsPage.PERSONAL_GITHUB_ACCOUNT_LOGIN_KEY) ?? '').trim();
      if (installationId && !String(this.githubContext.personalInstallationId ?? '').trim()) {
        this.githubContext.personalInstallationId = installationId;
        this.githubContext.activePersonalInstallationId = installationId;
      }
      if (accountLogin && !String(this.githubContext.personalAccountLogin ?? '').trim()) {
        this.githubContext.personalAccountLogin = accountLogin;
      }
    } catch {
      // Ignore local storage failures.
    }
  }

  private clearPersonalGithubFromLocal(): void {
    try {
      localStorage.removeItem(SettingsPage.PERSONAL_GITHUB_INSTALLATION_ID_KEY);
      localStorage.removeItem(SettingsPage.PERSONAL_GITHUB_ACCOUNT_LOGIN_KEY);
    } catch {
      // Ignore local storage failures.
    }
  }

  modelLabel(modelId: string | null | undefined): string {
    const normalized = String(modelId ?? '').trim();
    return this.modelOptions.find((option) => option.id === normalized)?.label ?? normalized;
  }

  surfaceModelValue(surfaceKey: string): string {
    return String(this.integrationPreferences.surfaceModelDefaults?.[surfaceKey] ?? '').trim();
  }

  onSurfaceModelChange(surfaceKey: string, value: string): void {
    const key = String(surfaceKey ?? '').trim();
    if (!key) return;
    const next = String(value ?? '').trim();
    if (!next) {
      delete this.integrationPreferences.surfaceModelDefaults[key];
      this.integrationPreferences.surfaceModelDefaults = { ...this.integrationPreferences.surfaceModelDefaults };
      return;
    }
    this.integrationPreferences.surfaceModelDefaults = {
      ...this.integrationPreferences.surfaceModelDefaults,
      [key]: next,
    };
  }
}
